// Copyright (c) StrangeBee 2023

import { FormSubmitButton, FormSubmitOptions } from '@Atoms/Buttons/FormSubmitButton/FormSubmitButton';
import client from '@Client';
import { useLocalMessage } from '@Helpers/locale';
import useListQuery from '@Hooks/useListQuery';
import useValidateForm from '@Hooks/useValidateForm';
import THDrawer from '@Molecules/THDrawer/THDrawer';
import { Store } from 'rc-field-form/lib/interface';
import React, { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { AttackPatternsOutput, CatalogOutput } from 'thehive-sdk';
import { THForm } from '@Molecules/THForm/THForm';
import { THDatePicker } from '@Molecules/Inputs/THDatePicker/THDatePicker';
import { THSwitch } from '@Molecules/Inputs/THSwitch/THSwitch';
import { THMarkdown } from '@Molecules/Inputs/THMarkdown/THMarkdown';
import { normalizeDate } from '@Helpers/date';
import { THSelect } from '@Molecules/Inputs/THSelect/THSelect';
import { ProcedureMultiInput } from 'thehive-sdk';
import { Form } from 'antd';
import { TTPSelector } from './TTPSelector';

import './TTPCreate.scss';

type AttackPatternCustom = AttackPatternsOutput & { tacticParent: string; isTactic?: boolean; refKey?: string };

interface TTPFormValues extends FormSubmitOptions {
    catalog?: string;
    tactic: string;
    technique: AttackPatternCustom[];
    patternId: string[];
    occurDate: number;
    description: string;
}

interface TTPCreateProps {
    onFinish: (ttp: ProcedureMultiInput) => void;
    triggerElem?: ReactElement;
    visible?: boolean;
    onVisibleChange?: (visible: boolean) => void;
}

export const TTPCreate = ({ onFinish, triggerElem, onVisibleChange, visible }: TTPCreateProps): ReactElement => {
    const i = useLocalMessage();

    const { form, isValid, onFormValuesChange, fieldsTouched, setFieldsTouched } = useValidateForm();
    const [hasProcedure, setHasProcedure] = useState(false);
    const catalog = Form.useWatch(['catalog'], form);

    const { data: catalogList = [], refetch: refetchCatalogs } = useListQuery<CatalogOutput[]>({
        queryParams: {
            selector: {
                head: [{ name: 'listCatalog' }],
                name: 'get-list-catalog',
                extraData: ['tactics'],
            },
            paginationOptions: { itemsPerPage: 100, page: 0 },
            sort: ['+name'],
        },
        controller: client.AttackPatternsController,
        fetchOnStart: false,
        manual: true,
    });

    const qResult = useListQuery<AttackPatternsOutput[]>({
        queryParams: {
            selector: {
                head: [{ name: 'getCatalog', idOrName: catalog }, { name: 'patterns' }],
                name: `get-catalog-${catalog}-patterns`,
                initialFilters: [
                    { field: 'patternType', values: ['attack-pattern'], type: 'string', describeAttribute: '' as any },
                    { field: 'revoked', values: [false], type: 'boolean', describeAttribute: '' as any },
                ],
            },
            sort: ['+patternId'],
        },
        controller: client.AttackPatternsController,
        fetchOnStart: false,
        manual: true,
    });
    const { data: attackPatternList = [], refetch, isLoading } = qResult;

    useEffect(() => {
        if (!catalog && catalogList.length > 0) {
            const enterpriseCatalog = catalogList.find((c) => c.name.toLowerCase().includes('enterprise'));
            const selectedCatalog = enterpriseCatalog || catalogList[0];
            form.setFieldsValue({ catalog: selectedCatalog._id });
        }
    }, [catalogList, catalog, form]);

    useEffect(() => {
        if (catalog) {
            refetch();
        }
    }, [catalog]); //eslint-disable-line

    const onValuesChange = useCallback(
        (changedValue: Store): void => {
            if ('catalog' in changedValue) {
                form.setFieldsValue({
                    technique: undefined,
                });
            }
            onFormValuesChange();
        },
        [form, onFormValuesChange],
    );

    const catalogData = useMemo(() => {
        return catalogList.find((c) => c._id === catalog);
    }, [catalog, catalogList]);

    const attackPatternsSelector = useMemo(() => {
        if (attackPatternList.length) {
            const attackPatternTreated = attackPatternList.reduce((acc, curr) => {
                curr.tactics.forEach((tactic) => {
                    acc[tactic] = [
                        ...(acc[tactic] || []),
                        { ...curr, tacticParent: tactic, refKey: `${curr._id}-${tactic}` },
                    ];
                });

                return acc;
            }, {} as Record<string, AttackPatternCustom[]>);
            return Object.values(attackPatternTreated).flat();
        }
        return [];
    }, [attackPatternList]);

    const resetFields = useCallback(() => {
        setHasProcedure(false);
        setFieldsTouched(false);
        form.resetFields();
    }, [form, setFieldsTouched]);

    return (
        <THDrawer
            title={i('procedure.create.label')}
            className="drawer-form"
            triggerElem={triggerElem}
            onClose={() => resetFields()}
            afterVisibleChange={(visible) => {
                visible && refetchCatalogs();
                onVisibleChange?.(visible);
            }}
            visible={visible}
            disableClose={fieldsTouched}
        >
            {(toggleDrawer) => (
                <THForm<TTPFormValues>
                    name="ttp-form"
                    form={form}
                    onValuesChange={(changedValue) => {
                        onValuesChange(changedValue);
                        setFieldsTouched(true);
                    }}
                    onFinish={({ another, occurDate, description, technique }) => {
                        onFinish({
                            patternList: technique?.map(({ patternId, tacticParent }) => ({
                                patternId,
                                tactic: tacticParent,
                            })) as { tactic: string; patternId: string }[],
                            occurDate,
                            description,
                        });

                        if (!another) {
                            toggleDrawer();
                            resetFields();
                            return;
                        }

                        resetFields();
                    }}
                >
                    <THForm.Layout
                        footer={
                            <FormSubmitButton
                                disabled={!isValid || !fieldsTouched}
                                onCancel={() => {
                                    toggleDrawer();
                                    resetFields();
                                }}
                                formName="ttp-form"
                            />
                        }
                    >
                        <THForm.Item
                            label={i('procedure.create.catalog.label')}
                            name="catalog"
                            rules={[{ required: true, message: i('procedure.create.catalog.required') }]}
                        >
                            <THSelect
                                options={catalogList?.map(({ name, _id }) => ({ label: name, value: _id }))}
                                placeholder={i('procedure.create.catalog.placeholder')}
                            />
                        </THForm.Item>
                        <THForm.Item
                            label={i('procedure.create.occur.label')}
                            name="occurDate"
                            rules={[{ required: true, message: i('procedure.create.occur.required') }]}
                            normalize={normalizeDate}
                        >
                            <THDatePicker showTime />
                        </THForm.Item>

                        <THForm.Item
                            label={i('procedure.create.technique.label')}
                            name="technique"
                            hidden={catalog === ''}
                            rules={[{ required: true, message: i('procedure.create.technique.required') }]}
                        >
                            <TTPSelector
                                attackPatterns={attackPatternsSelector}
                                tactics={catalogData?.extraData.tactics || []}
                                isLoading={isLoading}
                            />
                        </THForm.Item>
                        <THForm.Item
                            label={i('procedure.create.procedure.label')}
                            extraAction={
                                <>
                                    {i('procedure.create.procedure.add')}
                                    <THSwitch checked={hasProcedure} onChange={(checked) => setHasProcedure(checked)} />
                                </>
                            }
                        >
                            <THForm.Item
                                name="description"
                                hidden={!hasProcedure}
                                className="ttp-create__form__description-item"
                                noStyle
                            >
                                <THMarkdown sameSize />
                            </THForm.Item>
                        </THForm.Item>
                    </THForm.Layout>
                </THForm>
            )}
        </THDrawer>
    );
};
